import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn } from '../../lib/utils';
import {
  Sparkles, Copy, FileText, Send, Download, Mail,
  ClipboardCopy, Loader2, CheckCircle2, User, Stethoscope,
  ChevronDown, ChevronUp, MessageSquare, Shield, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';
import { DEMO_VISIT_TYPES_CATALOG } from '../../lib/fixtures/schedulerV2Demo';
import { getVisitTypeNoteConfig, NARRATIVE_STRUCTURES } from '../../lib/demo/visitTypeNoteConfigStore';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  subjective: { label: 'Subjective', short: 'S', color: 'bg-blue-500' },
  objective: { label: 'Objective', short: 'O', color: 'bg-emerald-500' },
  assessment: { label: 'Assessment', short: 'A', color: 'bg-amber-500' },
  plan: { label: 'Plan', short: 'P', color: 'bg-violet-500' },
};

const LuminaSOAPNotes = () => {
  const { isDemo } = useAppMode();

  // Demo-mode visit-type selector — drives which note structure + copy-forward
  // rules + Assessment seeds the composer should use. Real mode keeps the
  // legacy free-form input path (selector hidden).
  const [selectedVisitTypeId, setSelectedVisitTypeId] = useState(
    () => (DEMO_VISIT_TYPES_CATALOG[0]?.id || null)
  );
  const activeVisitType = isDemo
    ? DEMO_VISIT_TYPES_CATALOG.find((v) => v.id === selectedVisitTypeId)
    : null;
  const activeNoteConfig = isDemo && activeVisitType
    ? getVisitTypeNoteConfig(activeVisitType.id, 'consult')
    : null;
  const activeStructureLabel = activeNoteConfig
    ? (NARRATIVE_STRUCTURES.find((s) => s.id === activeNoteConfig.narrativeStructure)?.label || 'Consult Report')
    : null;
  const enabledSectionLabels = activeNoteConfig
    ? (activeNoteConfig.sections || [])
        .filter((s) => s.enabled)
        .sort((a, b) => a.order - b.order)
        .map((s) => s.label)
    : [];

  // Input state
  const [patientName, setPatientName] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [medicalHistory, setMedicalHistory] = useState('');
  const [medications, setMedications] = useState('');
  const [vitals, setVitals] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Output state
  const [soap, setSoap] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);

  // Dialogs
  const [showCCDA, setShowCCDA] = useState(false);
  const [showDirectMsg, setShowDirectMsg] = useState(false);
  const [dmRecipient, setDmRecipient] = useState('');
  const [autoDirectMsg, setAutoDirectMsg] = useState(() => {
    try { return localStorage.getItem('lumina_auto_dm') === 'true'; } catch { return false; }
  });

  const handleGenerate = async () => {
    if (!patientName.trim() || !chiefComplaint.trim()) {
      toast.error('Patient name and chief complaint are required');
      return;
    }
    setGenerating(true);
    setSoap(null);
    try {
      const res = await fetch(`${API}/api/lumina/soap/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientName,
          chief_complaint: chiefComplaint,
          age, sex,
          medical_history: medicalHistory,
          medications,
          vitals,
          additional_context: additionalContext,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSoap(data.soap);
        toast.success('SOAP note generated');
        if (autoDirectMsg) {
          setShowDirectMsg(true);
        }
      } else {
        toast.error('Generation failed', { description: data.error });
      }
    } catch (err) {
      toast.error('Network error', { description: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const formatFullNote = () => {
    if (!soap) return '';
    return `SOAP Note — ${patientName}\n${'—'.repeat(40)}\n\nSUBJECTIVE:\n${soap.subjective}\n\nOBJECTIVE:\n${soap.objective}\n\nASSESSMENT:\n${soap.assessment}\n\nPLAN:\n${soap.plan}`;
  };

  const handleCopyNote = () => {
    navigator.clipboard?.writeText(formatFullNote());
    toast.success('Full SOAP note copied', { description: 'Ready to paste into any EHR' });
  };

  const handleCopySection = (section) => {
    if (!soap) return;
    navigator.clipboard?.writeText(soap[section]);
    toast.success(`${SECTION_LABELS[section].label} copied`);
  };

  const handleSectionEdit = (section, value) => {
    setSoap(prev => ({ ...prev, [section]: value }));
  };

  const handleDownloadCCDA = () => {
    const ccda = `<?xml version="1.0" encoding="UTF-8"?>
<ClinicalDocument xmlns="urn:hl7-org:v3">
  <title>SOAP Note — ${patientName}</title>
  <effectiveTime value="${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}" />
  <component>
    <structuredBody>
      <component><section><title>Subjective</title><text>${soap?.subjective || ''}</text></section></component>
      <component><section><title>Objective</title><text>${soap?.objective || ''}</text></section></component>
      <component><section><title>Assessment</title><text>${soap?.assessment || ''}</text></section></component>
      <component><section><title>Plan</title><text>${soap?.plan || ''}</text></section></component>
    </structuredBody>
  </component>
</ClinicalDocument>`;
    const blob = new Blob([ccda], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SOAP_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xml`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CCDA document downloaded');
    setShowCCDA(false);
  };

  const handleSendDirectMessage = () => {
    if (!dmRecipient.trim()) {
      toast.error('Please enter a recipient');
      return;
    }
    toast.success('Direct Message sent', { description: `SOAP note sent to ${dmRecipient}` });
    setShowDirectMsg(false);
    setDmRecipient('');
  };

  const toggleAutoDirectMsg = () => {
    const newVal = !autoDirectMsg;
    setAutoDirectMsg(newVal);
    localStorage.setItem('lumina_auto_dm', String(newVal));
    toast.success(newVal ? 'Auto Direct Message enabled' : 'Auto Direct Message disabled');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="soap-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">SOAP Notes</h1>
          <p className="text-sm text-slate-500 mt-0.5">Generate, copy, and send clinical notes instantly</p>
        </div>
        <button
          onClick={toggleAutoDirectMsg}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors",
            autoDirectMsg ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-500"
          )}
          data-testid="auto-dm-toggle"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Auto Direct Message: {autoDirectMsg ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* ── VISIT TYPE + NOTE CONFIG PREVIEW (demo-only) ── */}
      {isDemo && !soap && (
        <Card className="border-violet-200 bg-violet-50/40" data-testid="soap-visit-type-picker-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3 justify-between">
              <div>
                <p className="text-[12px] font-semibold text-slate-800">Visit type &amp; note structure</p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  The selected visit type determines the note skeleton, copy-forward rules, and how many
                  Assessment &amp; Plan blocks are seeded. Configure in Settings → Preferences or
                  Scheduler → Settings.
                </p>
              </div>
              <Select value={selectedVisitTypeId} onValueChange={setSelectedVisitTypeId}>
                <SelectTrigger className="w-52 h-9 text-[12px]" data-testid="soap-visit-type-select">
                  <SelectValue placeholder="Visit type" />
                </SelectTrigger>
                <SelectContent>
                  {DEMO_VISIT_TYPES_CATALOG.map((vt) => (
                    <SelectItem key={vt.id} value={vt.id} className="text-[12px]">{vt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeNoteConfig && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-200" data-testid="soap-narrative-badge">
                  {activeStructureLabel}
                </Badge>
                <span className="text-[10px] text-slate-500">
                  {enabledSectionLabels.length} section{enabledSectionLabels.length === 1 ? '' : 's'} · {activeNoteConfig.assessments?.seeds?.length || 0} A/P block{(activeNoteConfig.assessments?.seeds?.length || 0) === 1 ? '' : 's'} seeded
                </span>
                <span className="text-[10px] text-slate-400 truncate">
                  {enabledSectionLabels.slice(0, 6).join(' · ')}{enabledSectionLabels.length > 6 ? ' …' : ''}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── INPUT SECTION ── */}
      {!soap && (
        <Card className="border-slate-200" data-testid="soap-input-card">
          <CardContent className="p-6 space-y-5">
            {/* Primary fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Patient Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={isDemo ? 'e.g. John Smith' : 'Enter patient name'}
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    className="pl-10"
                    data-testid="input-patient-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Age</label>
                  <Input placeholder="e.g. 45" value={age} onChange={(e) => setAge(e.target.value)} data-testid="input-age" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Sex</label>
                  <Select value={sex} onValueChange={setSex}>
                    <SelectTrigger data-testid="input-sex"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Chief Complaint *</label>
              <Textarea
                placeholder="e.g. Persistent headaches for 2 weeks, right temporal, throbbing, 6/10 pain..."
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                className="min-h-20"
                data-testid="input-chief-complaint"
              />
            </div>

            {/* Advanced fields toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
              data-testid="toggle-advanced"
            >
              {showAdvanced ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showAdvanced ? 'Hide' : 'Show'} additional context (medical history, meds, vitals)
            </button>

            {showAdvanced && (
              <div className="space-y-4 pt-2 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Medical History</label>
                    <Textarea placeholder="e.g. HTN, DM type 2, hyperlipidemia..." value={medicalHistory} onChange={(e) => setMedicalHistory(e.target.value)} className="min-h-16" data-testid="input-history" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Current Medications</label>
                    <Textarea placeholder="e.g. Metformin 500mg BID, Lisinopril 10mg daily..." value={medications} onChange={(e) => setMedications(e.target.value)} className="min-h-16" data-testid="input-meds" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Vitals</label>
                    <Textarea placeholder="e.g. BP 138/88, HR 72, Temp 98.6, SpO2 98%..." value={vitals} onChange={(e) => setVitals(e.target.value)} className="min-h-16" data-testid="input-vitals" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Additional Context</label>
                    <Textarea placeholder="e.g. Patient reports increased work stress, no recent trauma..." value={additionalContext} onChange={(e) => setAdditionalContext(e.target.value)} className="min-h-16" data-testid="input-context" />
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !patientName.trim() || !chiefComplaint.trim()}
              className="w-full h-12 bg-black hover:bg-gray-800 text-white text-sm font-semibold gap-2"
              data-testid="generate-soap-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating SOAP Note...</>
              ) : (
                <><Sparkles className="h-4 w-4" /> Generate SOAP Note with AI</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── GENERATING ANIMATION ── */}
      {generating && (
        <div className="flex flex-col items-center justify-center py-16 space-y-4" data-testid="generating-state">
          <div className="relative">
            <div className="h-16 w-16 rounded-2xl bg-black flex items-center justify-center">
              <Sparkles className="h-7 w-7 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-sm font-medium text-slate-600">AI is generating your SOAP note...</p>
          <p className="text-xs text-slate-400">This usually takes 5–10 seconds</p>
        </div>
      )}

      {/* ── GENERATED NOTE ── */}
      {soap && !generating && (
        <div className="space-y-4" data-testid="soap-output">
          {/* Copy Note Bar — TOP */}
          <div className="bg-black rounded-xl p-4 flex items-center justify-between" data-testid="copy-bar-top">
            <div className="flex items-center gap-3">
              <ClipboardCopy className="h-5 w-5 text-white" />
              <div>
                <p className="text-sm font-semibold text-white">Copy Note to EHR</p>
                <p className="text-xs text-slate-400">Formatted and ready to paste into any system</p>
              </div>
            </div>
            <Button onClick={handleCopyNote} className="bg-white text-black hover:bg-slate-100 gap-2 font-semibold" data-testid="copy-note-btn-top">
              <Copy className="h-4 w-4" /> Copy Full Note
            </Button>
          </div>

          {/* Patient Header */}
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                <User className="h-4 w-4 text-slate-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{patientName}</p>
                <p className="text-xs text-slate-500">{[age && `${age}y`, sex].filter(Boolean).join(' · ') || 'SOAP Note'} · {new Date().toLocaleDateString()}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setSoap(null); }} className="text-xs gap-1.5" data-testid="new-note-btn">
              <Sparkles className="h-3 w-3" /> New Note
            </Button>
          </div>

          {/* SOAP Sections */}
          {Object.entries(SECTION_LABELS).map(([key, meta]) => (
            <Card key={key} className="overflow-hidden" data-testid={`soap-section-${key}`}>
              <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-b">
                <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center text-white text-xs font-bold", meta.color)}>
                  {meta.short}
                </div>
                <p className="text-sm font-semibold text-slate-800">{meta.label}</p>
                <div className="flex-1" />
                <button
                  onClick={() => setEditing(editing === key ? null : key)}
                  className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                  data-testid={`edit-${key}-btn`}
                >
                  {editing === key ? 'Done' : 'Edit'}
                </button>
                <button
                  onClick={() => handleCopySection(key)}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                  data-testid={`copy-${key}-btn`}
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <CardContent className="p-5">
                {editing === key ? (
                  <Textarea
                    value={soap[key]}
                    onChange={(e) => handleSectionEdit(key, e.target.value)}
                    className="min-h-32 text-sm font-mono"
                    data-testid={`edit-${key}-textarea`}
                  />
                ) : (
                  <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{soap[key]}</div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* ── POST-GENERATION OUTPUTS ── */}
          <div className="grid grid-cols-3 gap-3" data-testid="output-actions">
            {/* Generate CCDA */}
            <button
              onClick={() => setShowCCDA(true)}
              className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-all text-center"
              data-testid="action-ccda"
            >
              <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-violet-600" />
              </div>
              <p className="text-sm font-semibold text-slate-800">Generate CCDA</p>
              <p className="text-[10px] text-slate-500">Download structured document for EHR upload</p>
            </button>

            {/* Send Direct Message */}
            <button
              onClick={() => setShowDirectMsg(true)}
              className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all text-center"
              data-testid="action-direct-message"
            >
              <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-sm font-semibold text-slate-800">Send Direct Message</p>
              <p className="text-[10px] text-slate-500">Secure healthcare messaging standard</p>
            </button>

            {/* Copy & Paste */}
            <button
              onClick={handleCopyNote}
              className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all text-center"
              data-testid="action-copy"
            >
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <ClipboardCopy className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm font-semibold text-slate-800">Copy & Paste</p>
              <p className="text-[10px] text-slate-500">Works with any EHR system</p>
            </button>
          </div>

          {/* Copy Note Bar — BOTTOM */}
          <div className="bg-black rounded-xl p-4 flex items-center justify-between" data-testid="copy-bar-bottom">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-white" />
              <p className="text-xs text-slate-400">HIPAA-compliant · Structured · EHR-ready</p>
            </div>
            <Button onClick={handleCopyNote} className="bg-white text-black hover:bg-slate-100 gap-2 font-semibold" data-testid="copy-note-btn-bottom">
              <Copy className="h-4 w-4" /> Copy Full Note
            </Button>
          </div>
        </div>
      )}

      {/* ── CCDA DIALOG ── */}
      <Dialog open={showCCDA} onOpenChange={setShowCCDA}>
        <DialogContent className="max-w-md" data-testid="ccda-dialog">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-600" /> Generate CCDA Document
          </DialogTitle>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Convert this SOAP note into a structured CCDA (Consolidated Clinical Document Architecture) document. 
              This format is accepted by most EHR systems for import.
            </p>
            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
              <p className="text-xs font-semibold text-violet-700 mb-1">Document includes:</p>
              <ul className="text-xs text-violet-600 space-y-0.5">
                <li>· Subjective, Objective, Assessment, Plan</li>
                <li>· Patient demographics</li>
                <li>· Encounter date and metadata</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleDownloadCCDA} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white gap-2" data-testid="download-ccda-btn">
                <Download className="h-4 w-4" /> Download CCDA (.xml)
              </Button>
              <Button variant="outline" onClick={() => setShowCCDA(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── DIRECT MESSAGE DIALOG ── */}
      <Dialog open={showDirectMsg} onOpenChange={setShowDirectMsg}>
        <DialogContent className="max-w-lg" data-testid="direct-message-dialog">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" /> Send as Direct Message
          </DialogTitle>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-600">
              Send this SOAP note via Direct Messaging — the healthcare-standard secure messaging protocol. 
              Compatible with any Direct-enabled EHR or Health Information Exchange (HIE).
            </p>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Recipient (Direct Address)</label>
              <Input
                placeholder="e.g. dr.smith@direct.hospital.org"
                value={dmRecipient}
                onChange={(e) => setDmRecipient(e.target.value)}
                data-testid="dm-recipient-input"
              />
            </div>
            <div className="bg-slate-50 rounded-lg border p-4">
              <p className="text-xs font-semibold text-slate-600 mb-2">Message Preview</p>
              <div className="text-xs text-slate-500 max-h-32 overflow-y-auto whitespace-pre-wrap font-mono">
                {formatFullNote().slice(0, 500)}...
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSendDirectMessage} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="send-dm-btn">
                <Send className="h-4 w-4" /> Send Direct Message
              </Button>
              <Button variant="outline" onClick={() => setShowDirectMsg(false)}>Cancel</Button>
            </div>
            <p className="text-[10px] text-slate-400 flex items-center gap-1">
              <Shield className="h-3 w-3" /> Encrypted end-to-end per HISP standards
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaSOAPNotes;
